<template>
  <CallPlanningLayout :userId="userId" prop="user">
    <v-card class="inline-block mb-5 p-4" hide-details>
      <CallPlanningFrequencySum title="Approved" :sum="callFrequencySum" :numberOfTargets="numberOfTargets" />
      <CallPlanningFrequencySum title="Requested" :sum="callFrequencyRequestedSum" :numberOfTargets="numberOfTargets" />
    </v-card>
    <DataTable :data="tableData" :perPage="25" exportTitle="Call Planning" ref="dataTable">
      <template #menu>
        <div class="hvh-flex align-center">
          <v-btn flat class="hvh-btn hvh-btn--secondary ml-4" @click="exportToCSV">Export</v-btn>
          <div class="inline-block">
            <HvhHoverElement v-model="opened">
              <DropDownAbsolute v-model="opened">
                <v-btn flat class="hvh-btn hvh-btn--secondary m-0 add-button">Add</v-btn>

                <template #drop-down>
                  <div class="py-3">
                    <div class="add-button-item" @click="showAddPhysician = true">Add Physician</div>
                    <div class="add-button-item" @click="showAddPhysicianGroup = true">Add Group</div>
                    <div class="add-button-item" @click="showAddText = true">Add Text</div>
                  </div>
                </template>
              </DropDownAbsolute>
            </HvhHoverElement>
          </div>
          <v-btn flat class="hvh-btn hvh-btn--primary" :class="{ disabled: !hasToRequestApproval }" @click="requestApproval">Request Approval</v-btn>
        </div>
      </template>
      <template #head>
        <tr>
          <Th :prop="`physicianGroupList.${column.column}`" sort export v-for="column in columns" :key="column.column">{{ column.text }}</Th>
          <Th prop="callFrequency" sort export>Final Call Frequency</Th>
          <th></th>
          <th></th>
        </tr>
      </template>
      <template #body="{ item }">
        <tr>
          <td v-for="column in columns" :key="column.column">{{ item.physicianGroupList[column.column] || '-' }}</td>
          <td>
            <div class="hvh-flex">
              <div>
                <div class="w-25px" v-if="!item.pendingRequests.changeCallFrequency">
                  <span class="link" @click="requestChangeCallFrequency(item)">{{ item.callFrequency || '-' }}</span>
                </div>
                <div class="text-center" v-else>
                  <div class="no-wrap" v-for="(txt, ind) in getValueResponse(item, 'callFrequency', 'ChangeCallFrequency')" :key="ind">{{ txt }}</div>
                </div>
              </div>
              <CallPlaningRequests class="ml-2" :value="item" type="ChangeCallFrequency" />
            </div>
          </td>
          <td>
            <div class="hvh-flex">
              <div>
                <span class="link" @click="requestDelete(item)" v-if="!item.pendingRequests.delete">Request Deleting</span>
                <div class="text-center" v-else>
                  <span>{{ getResponse(item.pendingRequests.delete) }}</span>
                </div>
              </div>
              <CallPlaningRequests class="ml-2" :value="item" type="Delete" />
            </div>
          </td>
          <td class="text-center">
            <span class="link" @click="detailRequests = item.solvedRequests" v-if="item.solvedRequests">{{ item.solvedRequests.length }} Solved Request{{ item.solvedRequests.length > 1 ? 's' : '' }}</span>
          </td>
        </tr>
      </template>
    </DataTable>
    <Loader v-if="!data || isLoading" />

    <HvhModal title="Solved Requests" :value="true" maxWidth="80%" @input="detailRequests = null" v-if="detailRequests">
      <CallPlanningRequestDetails :items="detailRequests" />
    </HvhModal>

    <CallPlanningAddPhysician :brandId="brandId" :userId="userId" @addTarget="addTarget" @removeTarget="removeTarget" @input="showAddPhysician = false" v-if="showAddPhysician" />
    <CallPlanningAddPhysician :brandId="brandId" :userId="userId" :group="true" @addTarget="addTarget" @removeTarget="removeTarget" @input="showAddPhysicianGroup = false" v-if="showAddPhysicianGroup" />
    <CallPlanningAddText :brandId="brandId" :userId="userId" @addTarget="addTarget" @close="showAddText = false" v-if="showAddText" />
  </CallPlanningLayout>
</template>

<script>
import axios from '../../axios';
import CallPlanningLayout from '../../components/callPlanning/CallPlanningLayout.vue';
import CallPlanningAddPhysician from '../../components/callPlanning/CallPlanningAddPhysician.vue';
import CallPlanningAddText from '../../components/callPlanning/CallPlanningAddText.vue';
import CallPlanningChooseFrequency from '../../components/callPlanning/CallPlanningChooseFrequency.vue';
import CallPlanningFrequencySum from '../../components/callPlanning/CallPlanningFrequencySum.vue';
import CallPlanningRequestDetails from '../../components/callPlanning/CallPlanningRequestDetails.vue';
import DataTable from '../../components/common/dataTable/DataTable.vue';
import Th from '../../components/common/dataTable/Th.vue';
import HvhModal from '../../components/common/modal/HvhModal.vue';
import { createFormData, errorDebug, tryParse } from '../../libraries/common';
import DropDownAbsolute from '../../components/common/dropDown/DropDownAbsolute.vue';
import HvhHoverElement from '../../components/common/hover/HvhHoverElement.vue';
import CallPlaningRequests from '../../components/callPlanning/CallPlaningRequests.vue';
export default {
  components: { DataTable, Th, CallPlanningLayout, CallPlanningRequestDetails, HvhModal, CallPlanningFrequencySum, CallPlanningAddPhysician, CallPlanningAddText, DropDownAbsolute, HvhHoverElement, CallPlaningRequests, },
  title: 'Call Planning',
  data() {
    return {
      data: null,
      columns: [],
      isLoading: false,
      detailRequests: null,
      showAddPhysician: null,
      showAddPhysicianGroup: null,
      showAddText: false,
      opened: false,
      expanded: false,
    };
  },
  computed: {
    tableData() {
      return (this.data || [])
        .map(f => ({
          ...f,
          callFrequency: f.callFrequency || f.physicianGroupList.callFrequency,
          pendingRequests: f.requests
            .filter(f => !f.reviewedAt)
            .reduce((acc, f) => {
              var type = f.type.camelCase();
              acc[type] = acc[type] || [];
              acc[type].push(f);
              return acc;
            }, {}),
        }));
    },
    callFrequencySum() {
      return (this.data || []).sum(f => parseInt(f.callFrequency || f.physicianGroupList.callFrequency || 0));
    },
    callFrequencyRequestedSum() {
      return (this.data || [])
        .filter(f => !f.deleted)
        .sum(f => {
          var callPlanningRequest = f.requests.last(f => f.type === 'ChangeCallFrequency' && (!f.reviewedAt || f.isApproved));
          return parseInt(callPlanningRequest?.value || f.callFrequency || f.physicianGroupList.callFrequency || 0);
        });
    },
    hasToRequestApproval() {
      return (this.data || [])
        .any(f => f.requests.any(g => !g.isApprovalRequested));
    },
    brandId() {
      return this.$route.params.brandId;
    },
    userId() {
      return this.$route.params.userId || this.$store.state.user.user.email;
    },
    isManager() {
      return !!this.$route.params.userId;
    },
    additionalColumns() {
      return Object.keys((this.data || [])
        .reduce((acc, f) => { Object.keys(f.physicianGroupList.additionalData).forEach(key => acc[key] = true); return acc; }, {}));
    },
    numberOfTargets() {
      return (this.data || []).sum(f => parseInt(f.physicianGroupList.callFrequency || 0));
    },
  },
  methods: {
    getData() {
      this.isLoading = true;
      return axios.get(`/api/CallPlanning/${this.brandId}/User/${this.userId}/BrandTargets`)
        .then(({ data }) => this.data = data
          .map(f => ({
            ...f,
            physicianGroupList: { ...f.physicianGroupList, ...(tryParse(f.physicianGroupList.additionalData) || {}) },
          })))
        .catch(errorDebug)
        .then(() => this.isLoading = false);
    },
    requestChanges(item, data) {
      this.isLoading = true;
      return axios.post(`/api/CallPlanning/${this.brandId}/User/${this.userId}/${item.id}/Request`, createFormData(data))
        .then(({ data }) => {
          this.data = [...this.data];
          var changedItem = this.data.find(f => f.id === item.id);
          changedItem.requests.push(data);
          this.$notify('Request saved. It will be sent to approval once you REQUEST APPROVAL.');
        })
        .catch(errorDebug)
        .then(() => this.isLoading = false);
    },
    requestDelete(item) {
      this.$confirm('Request deleting the account', `You are about to request account deletion ${item.physicianGroupList.organizationName}.`,
        {
          optionMessage: 'Give us the reason for deleting this account',
          optionComponent: 'v-textarea',
          optionRequired: true,
        })
        .then(reason => this.requestChanges(item, { reason, type: 'delete' }))
        .catch(() => null);
    },
    requestChangeCallFrequency(item) {
      this.$confirm('Request changing Call Frequency', `You are about to request changing call frequency for ${item.physicianGroupList.organizationName}.`,
        {
          optionMessage: 'Give us the new value for the call frequency',
          optionComponent: CallPlanningChooseFrequency,
          optionRequired: true,
          option: {},
        })
        .then(data => data.value != item.callFrequency && this.requestChanges(item, { ...data, type: 'changeCallFrequency' }))
        .catch(() => null);
    },
    requestApproval() {
      this.isLoading = true;
      axios.post(`/api/CallPlanning/${this.brandId}/User/${this.userId}/RequestApproval`)
        .then(this.getData)
        .catch(errorDebug)
        .then(() => this.isLoading = false);
    },
    getValueResponse(item, prop, type) {
      var last = item.pendingRequests[type.camelCase()].last();
      if (!last.reviewedAt)
        return ['Pending approval', `(from ${item[prop] || item.physicianGroupList[prop] || '-'} to ${last.value})`];
      return [`Request ${last.isApproved ? 'approved' : 'rejected'}`];
    },
    getResponse(requests) {
      var last = requests.last();
      if (!last.reviewedAt)
        return 'Pending approval';
      return `Request ${last.isApproved ? 'approved' : 'rejected'}`;
    },
    addTarget(event) {
      this.data.push(event);
    },
    removeTarget(event) {
      this.data = this.data.filter(f => f.id !== event.id);
    },
    exportToCSV() {
      this.$refs.dataTable.exportToCSV();
    },
    fullName(item) {
      return [item.physicianFirstName, item.physicianLastName]
        .filter(f => f)
        .join(' ');
    },
  },
  watch: {
    brandId() {
      this.data = null;
      this.getData();
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisCallPlanningUser'] = this;

    this.isLoading = true;
    axios.get(`/api/CallPlanning/Brands/${this.brandId}/Columns`)
      .then(({ data }) => this.columns = tryParse(data).filter(f => f.show))
      .then(this.getData)
      .catch(errorDebug)
      .then(() => this.isLoading = false);
  },
}
</script>

<style lang="scss" scoped>
.link {
  color: #002f6c;
  border-bottom: 1px solid #002f6c;
  cursor: pointer;
}

.add-button {
  width: 200px;
}

.add-button-item {
  font-weight: 600;
  font-size: 1.0714285714rem;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  padding: 6px 20px;
  cursor: pointer;

  &:hover {
    background-color: $color-gray-2;
  }
}
</style>